import { useRef, useState } from "react";
import { AlertCircle, FileSpreadsheet, Upload } from "lucide-react";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { cx } from "./cx";

export interface FileDropFile {
  /** Shown in the mono face, e.g. "st-alternator-masterfile.csv". */
  name: string;
  /** One muted line: what was found, e.g. "54 rows · 18 KB". */
  meta?: string;
}

export interface FileDropProps {
  /** Passed to the file picker. Dropped files are not filtered; check them in onFile. */
  accept?: string;
  label?: string;
  /** What's accepted and the limits, e.g. ".csv, up to 5,000 rows". */
  hint?: string;
  /** Once set, the zone collapses to a file row. */
  file?: FileDropFile | null;
  /** Shows a progress bar under the file row. */
  reading?: boolean;
  /** Says what's happening while reading. Never a percentage on its own. */
  progressLabel?: string;
  /** A problem with the file chosen (wrong type). Problems inside it belong to the screen. */
  error?: string | null;
  onFile?: (file: File) => void;
  disabled?: boolean;
  className?: string;
}

/** Drop zone and file picker for one file; a file row once chosen. The whole box is clickable. */
export function FileDrop({ accept = ".csv", label = "Drop your file here", hint, file, reading, progressLabel, error, onFile, disabled, className }: FileDropProps) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const pick = () => !disabled && input.current?.click();
  const take = (files: FileList | null) => {
    const chosen = files?.[0];
    if (chosen) onFile?.(chosen);
  };
  const problem = error && (
    <div role="alert" className="mt-2 flex items-start gap-2 type-small text-danger-700">
      <Icon icon={AlertCircle} size={16} className="mt-0.5 flex-none" />
      <span>{error}</span>
    </div>
  );

  if (file) {
    return (
      <div className={className}>
        <div className="flex flex-col gap-3 rounded-md border border-border-default bg-surface-card p-4 shadow-xs">
          <div className="flex items-center gap-3">
            <span className="grid size-10 flex-none place-items-center rounded-md bg-surface-tint-green text-green-700">
              <Icon icon={FileSpreadsheet} size={22} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate type-sku [font-size:var(--text-base)] text-text-strong">{file.name}</span>
              {file.meta && <span className="type-small text-text-muted">{file.meta}</span>}
            </span>
          </div>
          {reading && (
            <div className="flex flex-col gap-2">
              {/* The server checks the whole file at once, so the bar runs without a figure. */}
              <div role="progressbar" aria-label={progressLabel ?? "Reading the file"} className="h-1.5 overflow-hidden rounded-[3px] bg-paper-2">
                <div className="h-full w-1/3 animate-pulse rounded-[3px] bg-green-700" />
              </div>
              {progressLabel && <span aria-live="polite" className="type-small text-text-muted">{progressLabel}</span>}
            </div>
          )}
        </div>
        {problem}
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-label={`${label}. Choose a file`}
        onClick={pick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            pick();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          if (!disabled) take(event.dataTransfer.files);
        }}
        className={cx(
          "flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center outline-none [transition:var(--transition-control)]",
          "focus-visible:ring-2 focus-visible:ring-green-700",
          error ? "border-danger-500" : over ? "border-green-700" : "border-border-strong",
          over ? "bg-surface-tint-green" : disabled ? "cursor-not-allowed bg-surface-sunken" : "cursor-pointer bg-surface-card",
        )}
      >
        <span className={cx("grid size-12 place-items-center rounded-full text-green-700", over ? "bg-surface-card" : "bg-surface-tint-green")}>
          <Icon icon={Upload} size={24} />
        </span>
        <span className="type-h4 text-text-strong">{over ? "Drop it to upload" : label}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          tabIndex={-1}
          onClick={(event) => {
            event.stopPropagation();
            pick();
          }}
        >
          Choose a file
        </Button>
        {hint && <span className="type-small text-text-muted">{hint}</span>}
      </div>
      <input
        ref={input}
        type="file"
        accept={accept}
        tabIndex={-1}
        aria-hidden="true"
        className="hidden"
        onChange={(event) => {
          take(event.target.files);
          event.target.value = "";
        }}
      />
      {problem}
    </div>
  );
}
